/**
 * @file PaperworkSendModal.js
 * @version 1.5.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-30
 * @modified 2026-10-20
 * @description Sending transport paperwork — driver packs and mill order
 *              summaries — from the Transport and Bag boards.
 *
 *              One row per recipient: a switch, who it is and how many orders,
 *              and the address(es) it will go to. Addresses are shown, not
 *              edited: they live in Transport Settings (Mill Emails, Drivers,
 *              Hauliers), so a correction made there is the one used next time
 *              too. A row with no address cannot be switched on — unless
 *              "Send everything to the office instead" is on, which sends each
 *              recipient's email, unchanged, to the office.
 *
 *              An optional notice (EmailNoticeField) goes at the top of every
 *              email, under the greeting; it is handed to the caller's send as
 *              onSend({ notice }).
 *
 *              The caller owns the recipient list and the send; this is layout.
 *
 *              1.3.0 (2026-10-12): `warning` — a sentence shown at the top
 *              that does not stop the send (self-billed POs not planned and
 *              left out of the driver paperwork — utils/selfBilledPo).
 *
 *              1.4.0 (2026-10-14): driver paperwork straight to the drivers
 *              (Cam — packs forwarded from admin@ bounced at iCloud):
 *              - `warning` may be a list (each its own SendWindowWarning);
 *              - a row's `note` is said under its address (a pack going to
 *                the haulier's address because the driver has none);
 *              - a row with `canAddEmail` offers "Add <name>'s email": typed
 *                here, saved by the caller's `onSaveEmail(key, email)` (the
 *                driver's record — PUT /api/transport/drivers/:id/email);
 *              - `onCopyToOfficeChange` given → "Also send a copy to the
 *                office" (off by default): each email Cc's the office; the
 *                recipient stays the recipient. Greyed while everything is
 *                going to the office anyway.
 *
 *              1.5.0 (2026-10-20, Cam): `layoutChoice` given (driver paperwork
 *              on the Transport board) → each row has a "Detailed day sheet"
 *              switch, off for every driver on every send: on, that driver's
 *              top sheet is the lorry day sheet (Collect / D1 / D2, the
 *              compartments); off, one row per order, ForFarmers orders with
 *              their compartments. `layoutChoice.available` false greys every
 *              switch with `layoutChoice.reason` (a day under the old rules,
 *              or lorry day planning switched off). The row's `detailed` is
 *              the caller's; `onDetailedChange(key, on)` changes it.
 */

import React, { useState } from 'react';
import Modal from './Modal';
import Input from './Input';
import { useSlideOutButton } from './SlideOutButton';
import EmailNoticeField from './EmailNoticeField';
import SenderSelect from './SenderSelect';
import SendWindowWarning from './SendWindowWarning';
import './PaperworkSendModal.css';

const EMAIL_RE = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;
const sameAddress = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();

/**
 * "Add <name>'s email" on one row: a box, Save and Cancel. Saving is the
 * caller's (onSave resolves, or throws with the reason to show); the row then
 * shows the new address.
 */
function AddEmail({ rowKey, name, onSave, disabled }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  if (!open) {
    return (
      <button type="button" className="psm-link psm-add-email" disabled={disabled}
        onClick={() => { setOpen(true); setError(null); }}>
        Add {name}'s email
      </button>
    );
  }
  const close = () => { setOpen(false); setDraft(''); setError(null); };
  const save = async () => {
    const email = draft.trim();
    if (!EMAIL_RE.test(email)) {
      setError('Type one email address');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(rowKey, email);
      close();
    } catch (e) {
      setError(e?.message || 'Could not save the address');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="psm-add-email-form">
      <Input type="email" value={draft} placeholder={`${name}'s email`} autoFocus aria-label={`${name}'s email`}
        onChange={(e) => setDraft(e.target.value)} onEnter={save} disabled={saving} />
      <button type="button" className={`btn-icon${saving ? ' btn-disabled' : ''}`} disabled={saving}
        title="Save to the driver — used from now on" aria-label="Save the address" onClick={save}>
        <span className="material-symbols-rounded">check</span>
      </button>
      <button type="button" className={`btn-icon${saving ? ' btn-disabled' : ''}`} disabled={saving}
        title="Cancel" aria-label="Cancel" onClick={close}>
        <span className="material-symbols-rounded">close</span>
      </button>
      {error && <span className="psm-add-email-error" role="alert">{error}</span>}
    </div>
  );
}

function Body({ recipients, onToggle, onToggleAll, sendToOffice, onSendToOfficeChange, officeEmail,
  generating, generatingText, onSend, onClose, sendNoun, settingsHint, intro, user, warning = null,
  copyToOffice = false, onCopyToOfficeChange = null, onSaveEmail = null,
  layoutChoice = null, onDetailedChange = null }) {
  const sendable = (r) => sendToOffice || (r.emails || []).length > 0;
  const chosen = recipients.filter(r => r.include && sendable(r));
  const allOn = recipients.filter(sendable).every(r => r.include);
  // Lives as long as the window is open; a failed send keeps it for a retry.
  const [notice, setNotice] = useState('');
  // admin@ by default; "send as me" is the signed-in user's own address (server-resolved).
  const [sendAs, setSendAs] = useState('admin');
  useSlideOutButton([chosen.length, sendToOffice, generating]);
  // The per-driver layout switch (driver paperwork only).
  const showLayout = !!(layoutChoice && onDetailedChange);
  const layoutOpen = showLayout && layoutChoice.available === true;
  const layoutBlock = !showLayout || layoutOpen ? null
    : (layoutChoice.loading ? 'Checking how this day was planned…' : (layoutChoice.reason || 'Not available for this day'));

  if (generating) {
    return (
      <div className="psm-progress">
        <span className="material-symbols-rounded spinning-anti">sync</span>
        <p>{generatingText}</p>
        <p className="psm-muted">This may take a moment.</p>
      </div>
    );
  }

  return (
    <>
      {intro && <p className="psm-intro">{intro}</p>}
      {[].concat(warning || []).filter(Boolean).map((w, i) => <SendWindowWarning key={i} message={w} />)}

      <div className={`psm-office${sendToOffice ? ' on' : ''}`}>
        <div className="checkbox-container">
          <div className="check light">
            <input id="psm-office" type="checkbox" checked={sendToOffice}
              onChange={(e) => onSendToOfficeChange(e.target.checked)} />
            <label htmlFor="psm-office"></label>
          </div>
        </div>
        <label htmlFor="psm-office" className="psm-office-text">
          <strong>Send everything to the office instead</strong>
          <span>Each email goes to {officeEmail}, not to the {sendNoun}s below.</span>
        </label>
      </div>

      {onCopyToOfficeChange && (
        <div className={`psm-office psm-copy${copyToOffice && !sendToOffice ? ' on' : ''}${sendToOffice ? ' disabled' : ''}`}
          title={sendToOffice ? 'Everything is going to the office already' : undefined}>
          <div className="checkbox-container">
            <div className="check light">
              <input id="psm-copy-office" type="checkbox" checked={copyToOffice && !sendToOffice} disabled={sendToOffice}
                onChange={(e) => onCopyToOfficeChange(e.target.checked)} />
              <label htmlFor="psm-copy-office"></label>
            </div>
          </div>
          <label htmlFor="psm-copy-office" className="psm-office-text">
            <strong>Also send a copy to the office</strong>
            <span>
              {sendToOffice
                ? 'Everything is going to the office already.'
                : `Each ${sendNoun} still gets their own email; ${officeEmail} is copied in (Cc).`}
            </span>
          </label>
        </div>
      )}

      <div className="psm-list-head">
        <span>{recipients.length} {sendNoun}{recipients.length === 1 ? '' : 's'}</span>
        <button type="button" className="psm-link" onClick={() => onToggleAll(!allOn)}>
          {allOn ? 'Switch all off' : 'Switch all on'}
        </button>
      </div>

      {showLayout && (
        <p className={`psm-layout-hint${layoutOpen ? '' : ' disabled'}`}>
          <span className="material-symbols-rounded">{layoutOpen ? 'view_list' : 'block'}</span>
          {layoutOpen
            ? 'Each top sheet lists one row per order (ForFarmers orders with their compartments). Switch on "Detailed day sheet" for a driver to send the lorry day sheet instead: Collect, D1, D2… with the compartments for each order.'
            : `${layoutBlock}.`}
        </p>
      )}

      <ul className={`psm-list${showLayout ? ' with-layout' : ''}`}>
        {recipients.map((r) => {
          const can = sendable(r);
          const on = r.include && can;
          return (
            <li key={r.key} className={`psm-row${on ? ' on' : ''}${can ? '' : ' no-address'}`}>
              <div className="checkbox-container">
                <div className="check light">
                  <input id={`psm-${r.key}`} type="checkbox" checked={on} disabled={!can}
                    onChange={(e) => onToggle(r.key, e.target.checked)} />
                  <label htmlFor={`psm-${r.key}`}></label>
                </div>
              </div>
              <div className="psm-who">
                <span className="psm-name">{r.label}</span>
                <span className="psm-sub">{r.sublabel}</span>
              </div>
              <div className="psm-to">
                {sendToOffice ? (
                  <span className="psm-chip office">
                    <span className="material-symbols-rounded">apartment</span>{officeEmail}
                  </span>
                ) : (r.emails || []).length ? (
                  r.emails.map(e => <span key={e} className={`psm-chip${r.note ? ' fallback' : ''}`}>{e}</span>)
                ) : (
                  <span className="psm-missing">
                    <span className="material-symbols-rounded">warning</span>
                    No address — add one in {r.settingsHint || settingsHint}
                  </span>
                )}
                {!sendToOffice && copyToOffice && can && !(r.emails || []).some(e => sameAddress(e, officeEmail)) && (
                  <span className="psm-chip office" title={`A copy goes to ${officeEmail}`}>
                    <span className="material-symbols-rounded">content_copy</span>Cc {officeEmail}
                  </span>
                )}
                {!sendToOffice && r.emailNote && <span className="psm-note">{r.emailNote}</span>}
                {!sendToOffice && r.note && (
                  <span className="psm-note psm-fallback-note">
                    <span className="material-symbols-rounded">forward_to_inbox</span>{r.note}
                  </span>
                )}
                {!sendToOffice && r.canAddEmail && onSaveEmail && (
                  <AddEmail rowKey={r.key} name={r.name || 'the driver'} onSave={onSaveEmail} disabled={generating} />
                )}
              </div>
              {showLayout && (
                <div className={`psm-layout${layoutOpen ? '' : ' disabled'}${layoutOpen && r.detailed ? ' on' : ''}`}
                  title={layoutOpen
                    ? (r.detailed ? 'Lorry day sheet: Collect, D1, D2… with compartments' : 'One row per order (ForFarmers with their compartments)')
                    : layoutBlock}>
                  <div className="checkbox-container">
                    <div className="check light">
                      <input id={`psm-detail-${r.key}`} type="checkbox" checked={layoutOpen && !!r.detailed} disabled={!layoutOpen}
                        aria-label={`Detailed day sheet for ${r.name || 'this driver'}`}
                        onChange={(e) => onDetailedChange(r.key, e.target.checked)} />
                      <label htmlFor={`psm-detail-${r.key}`}></label>
                    </div>
                  </div>
                  <label htmlFor={`psm-detail-${r.key}`} className="psm-layout-text">
                    <strong>Detailed day sheet</strong>
                    <span>{layoutOpen && r.detailed ? 'Collect / D1 / D2' : 'One row per order'}</span>
                  </label>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <EmailNoticeField id={`psm-notice-${sendNoun}`} value={notice} onChange={setNotice} many />

      <div className="psm-sender">
        <span className="psm-sender-label">Send from</span>
        <div className="psm-sender-select">
          <SenderSelect user={user} value={sendAs} onChange={setSendAs} only={['admin', 'me']} disabled={generating} />
        </div>
      </div>

      <div className="psm-actions">
        <div className="buttons slideout">
          <div>
            <button className="btn-slideout" onClick={onClose}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          <div>
            <button className="btn-slideout" onClick={() => onSend({ notice, sendAs })} disabled={chosen.length === 0}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">send</span></div>
              <span className="btn-label">
                {sendToOffice
                  ? `Send ${chosen.length} to the office`
                  : `Send to ${chosen.length} ${sendNoun}${chosen.length === 1 ? '' : 's'}`}
              </span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function PaperworkSendModal({ isOpen, onClose, title, generating, ...rest }) {
  return (
    <Modal isOpen={isOpen} onClose={generating ? undefined : onClose} title={title} size="lg"
      className="paperwork-send" closeOnOverlayClick={!generating} closeOnEscape={!generating}>
      {isOpen && <Body onClose={onClose} generating={generating} {...rest} />}
    </Modal>
  );
}

export default PaperworkSendModal;
